import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createRefetchContainer, graphql } from 'react-relay/legacy';
import serverVars from 'server-vars';
import { getBuyerId, getGuestId } from 'dibs-cookie-jar';
import { CUSTOMER_TYPE_TRADE } from '../../constants/userConstants';
import { sbRespRefineMenuFiltersMap } from '../SbRespRefineMenu/SbRespRefineMenuFilters/sbRespRefineMenuFiltersMap';
import { hasPersonalizedFilterQueryParam } from '../../shared/helpers/personalizedFiltersHelpers';
import { handleSbUserCountryCode } from '../helpers/handleSbUserCountryCode';

/* Components */
import { SharedUserAndRelayVarLoader } from '../../shared/SharedUserAndRelayVarLoader';
import { SbSharedRefetchContainer } from '../SbSharedRefetchContainer/SbSharedRefetchContainer';
import { SbRespComponents } from '../SbRespComponents/SbRespComponents';
import { SbSharedHead } from '../SbSharedHead/SbSharedHead';
import { SbSharedTrackingContainer } from '../SbSharedTrackingContainer/SbSharedTrackingContainer';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';
import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { SearchCorrectionsProvider } from '../useSearchCorrections';
import { RecentlyViewedItemsProvider } from 'dibs-recent-history/exports/RecentlyViewedItemsProvider';

/* Styles */
import './SbRespLayout.scss'; // temp fix for FINDING-13444, will eventually change base style in DBL
import { includeSponsoredListing } from '../../utils/abTestHelper';

class SbRespLayoutComponent extends Component {
    constructor() {
        super();

        this.handleSbRefetch = this.handleSbRefetch.bind(this);
        this.handleUserRefetch = this.handleUserRefetch.bind(this);

        this.relayVars = {};
    }

    handleUserRefetch(variables, ...args) {
        /**
         * Need to refetch sb items for trade users, the price depends on the `isTrade` variable
         * being set.
         *
         * Only do this after login, hence the server vars check, subsequent visits will depend on
         * the ut query param.
         */
        const { isClient, fetchSbOnUserChange } = variables || {};
        const hasPersonalizedFilter = isClient && hasPersonalizedFilterQueryParam();

        //Personal-rerank needs to refetch s&b after login
        const fetchSb =
            (variables.isTrade && serverVars.get('userType') !== CUSTOMER_TYPE_TRADE) ||
            fetchSbOnUserChange;

        const { viewer, relayVariables } = this.props;
        const { displayUriRef } = (viewer && viewer.itemSearch) || {};
        const { followSearchPages, priceBookName } = relayVariables || {};

        this.doRefetch(
            {
                ...variables,
                fetchSb,
                skipUser: false,
                hasPersonalizedFilter,
                priceBookName,
                followSearchPages: displayUriRef ? [displayUriRef] : followSearchPages,
            },
            ...args
        );
    }

    handleSbRefetch(variables, renderVariables, callback) {
        const addUserData = includeSponsoredListing();
        /**
         * In case "sponsored-listings" abTest is on, adding userData to relay vars for refetch,
         * cause initial page could be not eligible for sponsored items, but subsequent page could (eg. by removing filters).
         * graphql will decide if sponsored items should be added in the end.
         */
        const userData = addUserData
            ? {
                  personalizationId: getBuyerId(document.cookie) || '',
                  guestId: getGuestId(document.cookie) || '',
                  includeSponsoredItems: true,
              }
            : {};
        this.doRefetch({ ...variables, ...userData, fetchSb: true }, renderVariables, () => {
            callback();

            this.props.runNotificationQueue();
        });
    }

    doRefetch(variables, renderVariables, callback) {
        this.relayVars = {
            ...this.relayVars,
            ...variables,
            userCountryCode: handleSbUserCountryCode({
                userCountryCode: variables?.userCountryCode,
                fetchSb: variables?.fetchSb,
            }),
        };
        this.props.relay.refetch(
            this.relayVars,
            { ...this.relayVars, ...renderVariables, fetchSb: true },
            callback
        );
    }

    render() {
        const { viewer, reinitTracking, relay } = this.props;
        const user = viewer.user || null;
        const {
            itemSearch,
            auctionsItemSearch = null,
            regionalInfo = null,
            shippingFilters = null,
        } = viewer;

        return (
            <RecentlyViewedItemsProvider
                maxItems={this.context.isTablet ? 10 : 12}
                itemsPerPage={this.context.isTablet ? 5 : 6}
            >
                <SharedUserAndRelayVarLoader
                    onUserRefetch={this.handleUserRefetch}
                    environment={relay.environment}
                />
                <SearchCorrectionsProvider itemSearch={itemSearch}>
                    {({ searchCorrectionTerm }) => (
                        <CurrencyProvider
                            render={({ currency }) => (
                                <>
                                    <SbSharedTrackingContainer
                                        user={user}
                                        itemSearch={itemSearch}
                                        reinitTracking={reinitTracking}
                                        refineMenuFiltersMap={sbRespRefineMenuFiltersMap}
                                        searchCorrectionTerm={searchCorrectionTerm}
                                    />
                                    <SbSharedHead itemSearch={itemSearch} />

                                    <SbSharedRefetchContainer
                                        viewer={viewer}
                                        itemSearch={itemSearch}
                                        regionalInfo={regionalInfo}
                                        shippingFilters={shippingFilters}
                                        onSbRefetch={this.handleSbRefetch}
                                        shouldShowSpinner={false}
                                    />
                                    <SbRespComponents
                                        viewer={viewer}
                                        itemSearch={itemSearch}
                                        auctionsItemSearch={auctionsItemSearch}
                                        user={user}
                                        currency={currency}
                                        isClient={!!this.relayVars.isClient}
                                        shippingFilters={shippingFilters}
                                    />
                                </>
                            )}
                        />
                    )}
                </SearchCorrectionsProvider>
            </RecentlyViewedItemsProvider>
        );
    }
}

SbRespLayoutComponent.propTypes = {
    viewer: PropTypes.object,
    relay: PropTypes.object.isRequired,
    localeDomain: PropTypes.string.isRequired,
    runNotificationQueue: PropTypes.func.isRequired,
    reinitTracking: PropTypes.func,
    relayVariables: PropTypes.shape({
        followSearchPages: PropTypes.arrayOf(PropTypes.string),
        userCountryCode: PropTypes.string,
        priceBookName: PropTypes.string,
    }).isRequired,
};

SbRespLayoutComponent.defaultProps = {
    runNotificationQueue: () => {},
};

SbRespLayoutComponent.contextType = ServerVarsContext;

const mapStateToProps = state => {
    const { intlVariables, relayVariables } = state;

    return {
        localeDomain: intlVariables.localeDomain,
        relayVariables: relayVariables.variables,
    };
};

export const SbRespLayout = createRefetchContainer(
    connect(mapStateToProps)(SbRespLayoutComponent),
    {
        viewer: graphql`
            fragment SbRespLayout_viewer on Viewer
            @argumentDefinitions(
                hasPersonalizedFilter: { type: "Boolean", defaultValue: false }
                userZipCode: { type: "String", defaultValue: "" }
                fetchRegionalInfo: { type: "Boolean", defaultValue: false }
            ) {
                ...SbRespComponents_viewer
                itemSearch(
                    userId: $personalizationId
                    guestId: $guestId
                    uriRef: $uriRef
                    originalUrl: $originalUrl
                    first: $first
                    page: $page
                    localeOrigin: $localeOrigin
                    regions: $regions
                    disableForceFacet: $disableForceFacet
                    disableNonParameterizedUrlRedirects: $disableNonParameterizedUrlRedirects
                    userCountryCode: $userCountryCode
                    regionsList: $regionsList
                    includeSponsoredItems: $includeSponsoredItems
                    priceBookName: $priceBookName
                ) {
                    displayUriRef
                    # Used by SearchCorrectionsProvider
                    ...useSearchCorrections_itemSearch
                    ...SbSharedRefetchContainer_itemSearch
                    ...SbSharedTrackingContainer_itemSearch
                    ...SbSharedHead_itemSearch
                    ...SbRespComponents_itemSearch @include(if: $fetchSb)
                    ...SbRespFollowSearchBanner_itemSearch
                }
                auctionsItemSearch: itemSearch(
                    uriRef: $auctionsItemSearchUriRef
                    userId: $personalizationId
                    guestId: $guestId
                ) @include(if: $fetchAuctionsItemSearch) {
                    ...SbRespComponents_auctionsItemSearch
                }
                user(userId: $userId) @include(if: $fetchUser) {
                    ...SbSharedTrackingContainer_user
                    ...SbRespComponents_user
                }
                regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode)
                    @include(if: $fetchRegionalInfo) {
                    ...SbSharedRefetchContainer_regionalInfo
                }
                shippingFilters(
                    uriRef: $uriRef
                    zipCode: $userZipCode
                    countryCode: $userCountryCode
                ) @include(if: $hasPersonalizedFilter) {
                    ...SbSharedRefetchContainer_shippingFilters
                    ...SbRespComponents_shippingFilters
                }
            }
        `,
    },
    graphql`
        query SbRespLayoutRefetchQuery(
            $userId: String = ""
            $personalizationId: String = ""
            $guestId: String = ""
            $fetchUser: Boolean!
            $fetchSb: Boolean!
            $skipUser: Boolean = true
            $isTrade: Boolean!
            $includeSponsoredItems: Boolean
            $uriRef: String
            $originalUrl: String
            $first: Int!
            $page: Int!
            $localeOrigin: String = ""
            $showSeller: Boolean!
            $followSearchPages: [String]
            $followSearchTypes: [types] = [SEARCH, USER_PREFERENCE]
            $contentModuleId: String = ""
            $previewId: String = ""
            $regions: String = ""
            $hasPersonalizedFilter: Boolean!
            $userZipCode: String = ""
            $userCountryCode: String = ""
            $fetchRegionalInfo: Boolean = false
            $disableForceFacet: Boolean = true
            $isClient: Boolean!
            $disableNonParameterizedUrlRedirects: Boolean = false # Only used for static SB
            $fetchTileVideo: Boolean = false
            # AUCTIONS-1264 - Changing this argument might break carousel UI, make sure the requirements in the ticket description are met.
            $interiorPhotosCount: Int = 10
            $priceBookName: String
            $fetchAuctionsItemSearch: Boolean!
            $auctionsItemSearchUriRef: String
            $regionsList: [String]
        ) {
            viewer {
                ...SbRespComponents_viewer
                itemSearch(
                    userId: $personalizationId
                    guestId: $guestId
                    uriRef: $uriRef
                    originalUrl: $originalUrl
                    first: $first
                    page: $page
                    localeOrigin: $localeOrigin
                    regions: $regions
                    disableForceFacet: $disableForceFacet
                    disableNonParameterizedUrlRedirects: $disableNonParameterizedUrlRedirects
                    userCountryCode: $userCountryCode
                    regionsList: $regionsList
                    priceBookName: $priceBookName
                    includeSponsoredItems: $includeSponsoredItems
                ) @include(if: $fetchSb) {
                    displayUriRef
                    ...SbSharedRefetchContainer_itemSearch
                    ...SbRespComponents_itemSearch
                    ...SbSharedHead_itemSearch
                    ...SbSharedTrackingContainer_itemSearch
                }
                auctionsItemSearch: itemSearch(
                    uriRef: $auctionsItemSearchUriRef
                    userId: $personalizationId
                    guestId: $guestId
                ) @include(if: $fetchAuctionsItemSearch) {
                    ...SbRespComponents_auctionsItemSearch
                }
                user(userId: $userId) @include(if: $fetchUser) @skip(if: $skipUser) {
                    ...SbSharedTrackingContainer_user
                    ...SbRespComponents_user
                }
                regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode)
                    @include(if: $fetchRegionalInfo) {
                    ...SbSharedRefetchContainer_regionalInfo
                }
                shippingFilters(
                    uriRef: $uriRef
                    zipCode: $userZipCode
                    countryCode: $userCountryCode
                ) @include(if: $hasPersonalizedFilter) {
                    ...SbSharedRefetchContainer_shippingFilters
                    ...SbRespComponents_shippingFilters
                }
            }
        }
    `
);
